// components/Layout/LocationGridLayout.jsx
import React, { useState, useEffect } from "react";
import ImageModal from "../Utils/ImageModal";
import { MapPin, Eye, User, Clock } from "lucide-react";
import "../Styles/LocationGridLayout.css";

// New: quick-link button images
import weatherPredictionImg from "../../../../assets/images/weather-prediction.png";
import travelPlannerImg from "../../../../assets/images/travel-planner.png";

import {
  fetchLocationData,
  fetchLocationIdOnly,
  fetchCountryIdLocationId,
  fetchLocationNameYear,
  fetchUserIdLocationNameYear,
  fetchUserIdCountryIdLocationId,
  extractMediaByType,
} from "../Utils/fetchLocationData";

// ==================== MAIN COMPONENT ====================

const LocationGridLayout = ({ suserid, fetchMode, locationData, contentType }) => {
  const [gridState, setGridState] = useState({
    images:        [],
    formattedData: null,
    loading:       true,
    error:         null,
  });
  const [selectedImage, setSelectedImage] = useState(null);

  // ==================== FETCH BASED ON MODE ====================

  useEffect(() => {
    if (!locationData || fetchMode === "unknown") {
      setGridState({
        images:        [],
        formattedData: null,
        loading:       false,
        error:         "No valid location data.",
      });
      return;
    }

    let cancelled = false;

    const loadData = async () => {
      try {
        setGridState((prev) => ({ ...prev, loading: true, error: null }));

        let formattedData = null;

        switch (fetchMode) {
          case "userid-locationid":
            formattedData = await fetchLocationData(
              locationData.locationId,
              locationData.year
            );
            break;

          case "locationid-only":
            formattedData = await fetchLocationIdOnly(
              locationData.locationId
            );
            break;

          case "countryid-locationid":
            formattedData = await fetchCountryIdLocationId(
              locationData.countryId,
              locationData.locationId
            );
            break;

          case "locationname-year":
            formattedData = await fetchLocationNameYear(
              locationData.locationName,
              locationData.year
            );
            break;

          case "userid-locationname-year":
            formattedData = await fetchUserIdLocationNameYear(
              locationData.userId,
              locationData.locationName,
              locationData.year
            );
            break;

          case "userid-countryid-locationid":
            formattedData = await fetchUserIdCountryIdLocationId(
              locationData.userId,
              locationData.countryId,
              locationData.locationId
            );
            break;

          default:
            throw new Error(`Unknown fetch mode: ${fetchMode}`);
        }

        if (!formattedData) {
          throw new Error(`No data returned for mode: ${fetchMode}`);
        }

        const images = extractMediaByType(formattedData, contentType);

        if (!cancelled) {
          setGridState({ images, formattedData, loading: false, error: null });
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Failed to load data";
          console.error("[EventGrid] Error:", message);
          setGridState({
            images:        [],
            formattedData: null,
            loading:       false,
            error:         message,
          });
        }
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [fetchMode, locationData, contentType]);

  // ==================== HANDERS ====================

  const handleImageClick = (image) => setSelectedImage(image);
  const handleCloseModal = () => setSelectedImage(null);

  if (gridState.loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading {contentType}...</p>
      </div>
    );
  }

  if (gridState.error) {
    return <p className="error-message">{gridState.error}</p>;
  }

  const fd = gridState.formattedData;

  return (
    <div className="ep-page-wrapper">
      {/* ============================================ */}
      {/* LEFT RAIL: Quick-link buttons                */}
      {/* ============================================ */}
      <aside className="ep-quick-links" aria-label="Quick links">
      <a
        href={`http://weather.kleido.ai/service-request/${suserid}?userid=${locationData.userId}&locationname=${encodeURIComponent(locationData.locationName)}`}
        className="ep-quick-link-btn"
        title="Open Travel Planner"
      >
        <img
          src={travelPlannerImg}
          alt="Travel Planner"
          className="ep-quick-link-img"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <span className="ep-quick-link-label">Travel Planner</span>
      </a>

        <a
          href={`http://weather.kleido.ai/weather-predictions/${suserid}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ep-quick-link-btn"
          title="Open Weather Predictions"
        >
          <img
            src={weatherPredictionImg}
            alt="Travel Planner"
            className="ep-quick-link-img"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <span className="ep-quick-link-label"> Weather Predictions </span>
        </a>
      </aside>

      {/* ============================================ */}
      {/* MAIN CONTENT: location info + image grid     */}
      {/* ============================================ */}
      <div className="ep-grid-container" style={{ background: "rgba(36,36,45,0.2)" }}>
        <div className="ep-location-info">
          <h2 className="ep-location-type">
            {fd?.locationName || locationData?.locationName}
          </h2>
          {fd?.createdOn && (
            <p className="ep-location-date">
              <Clock size={16} />
              {new Date(fd.createdOn).toLocaleString("en-US", {
                year:   "numeric",
                month:  "long",
                day:    "numeric",
                hour:   "2-digit",
                minute: "2-digit",
              })}
            </p>
          )}
        </div>

        <div className="ep-grid-layout" style={{ background: "black", borderRadius: 10 }}>
          {gridState.images.length === 0 ? (
            <p className="no-content-message">
              No {contentType} available for this location
            </p>
          ) : (
            gridState.images.map((image, index) => (
              <div
                key={image.id || index}
                className="ep-image-card"
                onClick={() => handleImageClick(image)}
                role="button"
                tabIndex={0}
                aria-label={`View ${image.description || "image"}`}
                onKeyPress={(e) => { if (e.key === "Enter") handleImageClick(image); }}
                style={{ background: "rgba(36,36,45,0.2)", border: "1px solid darkslategray" }}
              >
                <div className="ep-image-container">
                  <img
                    src={image.imageurl || "https://placehold.co/400x300?text=No+Image"}
                    alt={image.description || "Event image"}
                    className="ep-image"
                    loading="lazy"
                  />
                </div>

                <div className="ep-card-info" style={{ color: "white" }}>
                  <div className="ep-user-info">
                    <div className="ep-user-avatar">
                      {image.user_avatar ? (
                        <img src={image.user_avatar} alt={image.posted_by || "User"} />
                      ) : (
                        <User size={20} />
                      )}
                    </div>
                    <div className="ep-user-details">
                      <p className="ep-username" style={{ color: "white" }}>
                        {image.posted_by || "Anonymous"}
                      </p>
                      <p className="ep-post-time" style={{ color: "white" }}>
                        {image.timestamp
                          ? new Date(image.timestamp).toLocaleDateString()
                          : "Recently"}
                      </p>
                    </div>
                  </div>

                  <div className="ep-card-meta">
                    {image.location && (
                      <div className="ep-meta-item">
                        <MapPin size={14} style={{ color: "white" }} />
                        <span style={{ color: "white" }}>{image.location}</span>
                      </div>
                    )}
                    <div className="ep-meta-item">
                      <Eye size={14} style={{ color: "white" }} />
                      <span style={{ color: "white" }}>{image.impressions || 0} views</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {selectedImage && (
          <ImageModal image={selectedImage} onClose={handleCloseModal} />
        )}
      </div>
    </div>
  );
};

export default LocationGridLayout;
